/**
 * CFO Report
 *
 * The money that came back and the money that never arrived, beside what each
 * patient was worth: refunds and refund rate, declines and decline rates, and
 * the P&L's Net Rev per patient for new and recurring patients — by day, week
 * or month, with the range's totals compared to the range before it.
 *
 * Every figure is read from the report that owns it (backend
 * services/cfoReport), so this page agrees with the P&L, the Daily Report and
 * Declines rather than offering a fourth version of their numbers. The table
 * is components/DailyReportTable with this report's own columns.
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { format, parse, subDays } from 'date-fns'
import { RefreshCw, Loader2, AlertCircle, CalendarDays, ArrowUpRight, ArrowDownRight } from 'lucide-react'

import { billingReportsAPI } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import { Card, CardContent, Button } from '../components/ui'
import DailyReportTable, { formatMoney, formatCount } from '../components/DailyReportTable'
import { DateRangePicker } from '../components/DateRangePicker'

const GRANULARITIES = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
]

const COLUMNS = [
  { group: 'Revenue', key: 'gross_revenue', label: 'Gross Revenue', format: 'money' },
  { group: 'Revenue', key: 'refund_count', label: 'Refunds', format: 'count' },
  { group: 'Revenue', key: 'refunds', label: 'Refunded', format: 'money' },
  { group: 'Revenue', key: 'refund_rate', label: 'Refund Rate', format: 'ratio', emphasis: true },
  { group: 'Revenue', key: 'net_revenue', label: 'Net of Refunds', format: 'money', emphasis: true },
  { group: 'Declines', key: 'attempts', label: 'Attempts', format: 'count' },
  { group: 'Declines', key: 'declined', label: 'Declined', format: 'count' },
  { group: 'Declines', key: 'declined_amount', label: 'Declined $', format: 'money' },
  { group: 'Declines', key: 'decline_rate', label: 'Decline Rate', format: 'ratio', emphasis: true },
  { group: 'Declines', key: 'checkout_failure_rate', label: 'New Customers Lost', format: 'ratio' },
  { group: 'Declines', key: 'renewal_failure_rate', label: 'Renewals Lost', format: 'ratio' },
  { group: 'New Patients', key: 'new_patients', label: 'Patients', format: 'count' },
  { group: 'New Patients', key: 'new_revenue_per_patient', label: 'Revenue / Pt', format: 'money' },
  { group: 'New Patients', key: 'new_net_rev_per_patient', label: 'Net Rev / Pt', format: 'money', emphasis: true },
  { group: 'Recurring Patients', key: 'recurring_patients', label: 'Patients', format: 'count' },
  { group: 'Recurring Patients', key: 'recurring_revenue_per_patient', label: 'Revenue / Pt', format: 'money' },
  { group: 'Recurring Patients', key: 'recurring_net_rev_per_patient', label: 'Net Rev / Pt', format: 'money', emphasis: true },
]

/** What a period's card leads with on a phone. */
const HEADLINE_KEYS = [
  ['gross_revenue', 'Gross revenue'],
  ['refund_rate', 'Refund rate'],
  ['decline_rate', 'Decline rate'],
  ['checkout_failure_rate', 'New lost'],
  ['new_net_rev_per_patient', 'Net Rev / new pt'],
  ['recurring_net_rev_per_patient', 'Net Rev / rec. pt'],
]

const percent = value => (value == null ? '—' : `${(value * 100).toFixed(1)}%`)

/**
 * The tile's movement against the prior range. Money moves by percent; a rate
 * moves by points, because "refund rate up 20%" from 2.5% to 3.0% is a claim
 * nobody can check in their head, and +0.5 pts is.
 */
function deltaOf(kind, current, prior) {
  if (current == null || prior == null) return null
  if (kind === 'ratio') {
    const pts = (current - prior) * 100
    return { value: pts, text: `${Math.abs(pts).toFixed(1)} pts` }
  }
  if (prior === 0) return null
  const pct = ((current - prior) / Math.abs(prior)) * 100
  return { value: pct, text: `${Math.abs(pct).toFixed(0)}%` }
}

/** `goodWhenDown` flips the colour, never the arrow — the arrow is the number's own direction. */
function StatTile({ label, value, prior, kind, goodWhenDown = false, detail, loading }) {
  const delta = deltaOf(kind, value, prior)
  const good = delta && (goodWhenDown ? delta.value <= 0 : delta.value >= 0)
  return (
    <Card className="h-full">
      <CardContent className="p-4">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="mt-1.5 text-2xl font-semibold tabular-nums">
          {loading
            ? <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            : kind === 'ratio' ? percent(value) : formatMoney(value)}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
          {!loading && delta && (
            <span className={`flex items-center gap-0.5 font-medium ${good ? 'text-emerald-600' : 'text-red-600'}`}>
              {delta.value >= 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
              {delta.text}
            </span>
          )}
          {!loading && <span>{prior == null ? 'no prior figure' : `vs ${kind === 'ratio' ? percent(prior) : formatMoney(prior)} prior`}</span>}
        </div>
        {!loading && detail && <div className="mt-0.5 text-xs text-muted-foreground">{detail}</div>}
      </CardContent>
    </Card>
  )
}

function getDefaultDateRange() {
  return { from: subDays(new Date(), 29), to: new Date() }
}

export default function CfoReportPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { currentClinicId } = useAuthStore()

  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isRefreshing, setIsRefreshing] = useState(false)

  const granularity = searchParams.get('granularity') || 'daily'

  const dateRange = useMemo(() => {
    const startParam = searchParams.get('start')
    const endParam = searchParams.get('end')
    if (startParam && endParam) {
      const from = parse(startParam, 'yyyy-MM-dd', new Date())
      const to = parse(endParam, 'yyyy-MM-dd', new Date())
      if (!isNaN(from.getTime()) && !isNaN(to.getTime())) return { from, to }
    }
    return getDefaultDateRange()
  }, [searchParams])

  const setParam = useCallback((entries) => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev)
      for (const [key, value] of Object.entries(entries)) params.set(key, value)
      return params
    })
  }, [setSearchParams])

  const setDateRange = useCallback((range) => {
    if (!range?.from || !range?.to) return
    setParam({ start: format(range.from, 'yyyy-MM-dd'), end: format(range.to, 'yyyy-MM-dd') })
  }, [setParam])

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await billingReportsAPI.getCfo({
        startDate: format(dateRange.from, 'yyyy-MM-dd'),
        endDate: format(dateRange.to, 'yyyy-MM-dd'),
        granularity,
      })
      if (response.data.success) setData(response.data.data)
    } catch (err) {
      console.error('[CFO-REPORT] Error:', err)
      setError(err.response?.data?.error?.message || err.message)
      setData(null)
    } finally {
      setLoading(false)
      setIsRefreshing(false)
    }
  }, [dateRange, granularity])

  useEffect(() => { fetchData() }, [fetchData, currentClinicId])

  // Newest first, as the Daily Report reads.
  const rows = useMemo(() => [...(data?.rows || [])].reverse(), [data])
  const t = data?.totals
  const p = data?.prior_totals
  const fees = data?.fees

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-lg sm:text-2xl font-bold truncate">CFO Report</h1>
          <p className="text-xs text-muted-foreground hidden sm:block">
            Refunds, declines and net revenue per patient for {data?.displayName || 'this clinic'}
            {data?.prior && ` · compared with ${data.prior.startDate} – ${data.prior.endDate}`}
          </p>
        </div>

        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          <div className="flex rounded-lg border border-input bg-background p-1">
            {GRANULARITIES.map(g => (
              <button
                key={g.value}
                onClick={() => setParam({ granularity: g.value })}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  granularity === g.value
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                {g.label}
              </button>
            ))}
          </div>
          <DateRangePicker dateRange={dateRange} onDateRangeChange={setDateRange} />
          <Button
            variant="outline"
            size="icon"
            onClick={() => { setIsRefreshing(true); fetchData() }}
            disabled={isRefreshing}
            className="h-8 w-8 sm:h-9 sm:w-9"
          >
            {isRefreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatTile label="Gross Revenue" kind="money" value={t?.gross_revenue} prior={p?.gross_revenue} loading={loading} />
        <StatTile
          label="Refunded" kind="money" goodWhenDown value={t?.refunds} prior={p?.refunds} loading={loading}
          detail={t?.refund_count != null ? `${formatCount(t.refund_count)} refunds` : 'refunds unavailable'}
        />
        <StatTile label="Refund Rate" kind="ratio" goodWhenDown value={t?.refund_rate} prior={p?.refund_rate} loading={loading} detail="refunded ÷ gross revenue" />
        <StatTile label="Net of Refunds" kind="money" value={t?.net_revenue} prior={p?.net_revenue} loading={loading} />
        <StatTile
          label="Decline Rate" kind="ratio" goodWhenDown value={t?.decline_rate} prior={p?.decline_rate} loading={loading}
          detail={t?.attempts != null ? `${formatCount(t.declined)} of ${formatCount(t.attempts)} attempts · ${formatMoney(t.declined_amount)}` : 'declines unavailable'}
        />
        <StatTile
          label="New Customers Lost" kind="ratio" goodWhenDown value={t?.checkout_failure_rate} prior={p?.checkout_failure_rate} loading={loading}
          detail={t?.checkout_people != null
            ? `${formatCount(t.checkout_people_failed)} of ${formatCount(t.checkout_people)} never paid · renewals ${percent(t.renewal_failure_rate)}`
            : 'declines unavailable'}
        />
        <StatTile
          label="Net Rev / New Patient" kind="money" value={t?.new_net_rev_per_patient} prior={p?.new_net_rev_per_patient} loading={loading}
          detail={t ? `${formatCount(t.new_patients)} patients · ${formatMoney(t.new_revenue_per_patient)} revenue each` : null}
        />
        <StatTile
          label="Net Rev / Recurring Patient" kind="money" value={t?.recurring_net_rev_per_patient} prior={p?.recurring_net_rev_per_patient} loading={loading}
          detail={t ? `${formatCount(t.recurring_patients)} patients · ${formatMoney(t.recurring_revenue_per_patient)} revenue each` : null}
        />
      </div>

      <Card className="border-0 bg-transparent shadow-none md:border md:bg-card md:shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <CalendarDays className="h-8 w-8 mb-2 opacity-40" />
              <p className="text-sm">Nothing in this range.</p>
            </div>
          ) : (
            <DailyReportTable
              rows={rows}
              totals={t}
              granularity={granularity}
              currentPeriodKey={data?.current_period_key}
              clinic={data?.clinic}
              columnSet={COLUMNS}
              headlineKeys={HEADLINE_KEYS}
            />
          )}
        </CardContent>
      </Card>

      <div className="text-xs text-muted-foreground space-y-1">
        <p>
          <strong>Gross Revenue</strong> is the P&amp;L&apos;s revenue. Charges refunded in full
          within a day are left out of it entirely, as never having been sales.
          <strong> Refunds</strong> are dated on the day the money went back, not the day of the
          sale, so <strong>Refund Rate</strong> is that period&apos;s refunds over that
          period&apos;s gross revenue: a cash ratio, not the share of a day&apos;s sales later refunded.
          {data?.refunds?.checked === false && ' Refunds cannot be read for this clinic (only clinics billed through Manifold), so those columns are blank.'}
        </p>
        <p>
          <strong>Decline Rate</strong> is declined payment attempts over all attempts, which is
          what a processor is judged on. People retry, so it overstates what was lost.{' '}
          <strong>New Customers Lost</strong> counts people instead: of everyone who tried a first
          payment in the period, the share who never succeeded in it. <strong>Renewals Lost</strong> is
          the same for subscription rebills. A customer declined on Monday who pays on Tuesday is
          lost on Monday&apos;s row and paid on Tuesday&apos;s; the range total counts them once, as paid.
          Checkouts abandoned before a card was entered are not declines and are not counted.
          {data?.declines?.checked === false && ' Declines cannot be read for this clinic, so those columns are blank.'}
        </p>
        <p>
          <strong>Net Rev / Pt</strong> is the P&amp;L&apos;s unit economics, run over each period on
          its own: revenue − COGS − shipping − ad spend
          {fees?.ghost ? ' − Ghost' : ''}{fees?.sales_commission ? ' − sales commission' : ''}, per
          patient. A patient is <strong>new</strong> in the period of their first-ever purchase, and
          all ad spend sits with new patients, so a heavy ad day reads low on new Net Rev for that
          reason alone. <strong>Recurring</strong> is everyone else charged or shipped to, including
          patients mid-way through a prepaid bundle who paid nothing that period. Refunds are not
          netted here, matching the P&amp;L.
        </p>
        <p>
          Per-patient figures do not add up across rows: a patient charged on two days is one
          patient in each day&apos;s row and one in the week&apos;s. The Total row and the tiles are
          read over the whole range, so they match the P&amp;L for the same dates.
        </p>
      </div>
    </div>
  )
}
